import { useEffect, useRef, useState } from 'react';

import SliderArrow from '@/fo/icons/base/icons/arrow24.svg';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiLandingProductCardBioderma } from '@/fo/ui/landing/product-card/bioderma';
import { staticMediaUrl } from '@/fo/util/image-loader';
import { addLandingProductToCart } from '@/fo/util/landing-prices';
import { reachMetrikaGoal } from '@/fo/util/metrics';

import { HydrabioTestProduct } from '../../lib/types';

import { styles } from './Test.styles';

const media = (path: string) =>
  staticMediaUrl(`landing/bioderma-hydrabio/${path}`);

function bgStyle(mobile: string, desktop: string) {
  return {
    '--bg-sm': `url(${media(mobile)})`,
    '--bg': `url(${media(desktop)})`
  } as React.CSSProperties;
}

const questions = [
  {
    title: 'Выберите тип кожи',
    answers: [
      {
        title: <>Сухая или склонная к{'\u202F'}сухости</>,
        products: ['7780']
      },
      {
        title: (
          <>Нормальная, комбинированная или склонная к{'\u202F'}жирности</>
        ),
        products: ['7779']
      }
    ]
  },
  {
    title: 'Какие средства предпочитаете для\u202Fочищения лица?',
    answers: [
      {
        title: (
          <>
            Смываемые средства
            <br />
            для очищения лица
            <br />
            (пенки, гели)
          </>
        ),
        products: ['7752']
      },
      {
        title: (
          <>
            Несмываемые средства
            <br />
            (мицеллярная вода)
          </>
        ),
        products: ['7656']
      }
    ]
  },
  {
    title: 'Замечаете ли вы возрастные изменения? (морщины, морщинки)',
    answers: [
      { title: 'Да', products: ['5135', '1397'] },
      { title: 'Нет', products: ['1398', '1397'] }
    ]
  }
];

const categories = ['clean', 'special-care', 'care'] as const;

export interface TestProps {
  products: HydrabioTestProduct[];
}

/** Диагностика: вопросы и рекомендованная рутина */
export function Test({ products }: TestProps) {
  const slots = styles();
  const testRef = useRef<HTMLElement>(null);
  const thankRef = useRef<HTMLDivElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const pendingScroll = useRef<'thank' | 'results' | 'test' | null>(null);
  const [step, setStep] = useState<'start' | 'quiz' | 'thank'>('start');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<(number | null)[]>(
    questions.map(() => null)
  );
  const [resultIds, setResultIds] = useState<string[]>([]);
  const [resultsVisible, setResultsVisible] = useState(false);

  useEffect(() => {
    let frame = 0;

    const run = () => {
      const targetName = pendingScroll.current;
      if (!targetName) return;

      const target =
        targetName === 'thank'
          ? thankRef.current
          : targetName === 'results'
            ? resultsRef.current
            : testRef.current;

      if (!target) {
        frame = requestAnimationFrame(run);
        return;
      }

      pendingScroll.current = null;
      const headerHeight = window.innerWidth >= 768 ? 120 : 56;
      const top =
        target.getBoundingClientRect().top + window.scrollY - headerHeight;

      window.scrollTo({ top, behavior: 'auto' });
    };

    frame = requestAnimationFrame(run);
    return () => cancelAnimationFrame(frame);
  }, [step, page, resultsVisible]);

  const currentAnswered = selected[page] !== null;

  const selectAnswer = (answerIndex: number) => {
    setSelected((prev) =>
      prev.map((value, index) => (index === page ? answerIndex : value))
    );
  };

  const openPage = (nextPage: number) => {
    if (nextPage === page) return;
    if (nextPage > page && !currentAnswered) return;
    setPage(nextPage);
  };

  const showResult = () => {
    const ids = questions.flatMap((question, index) => {
      const answerIndex = selected[index];
      if (answerIndex === null) return [];
      return question.answers[answerIndex].products;
    });

    setResultIds(ids);
    setStep('thank');
    setResultsVisible(false);
    pendingScroll.current = 'thank';
  };

  const restart = () => {
    setSelected(questions.map(() => null));
    setResultIds([]);
    setResultsVisible(false);
    setPage(0);
    setStep('quiz');
    pendingScroll.current = 'test';
  };

  const visibleCategories = categories
    .map((category) => ({
      category,
      name:
        products.find((product) => product.category === category)
          ?.categoryName ?? '',
      items: products.filter(
        (product) =>
          product.category === category &&
          (!resultsVisible || resultIds.includes(product.productId))
      )
    }))
    .filter((category) => category.items.length > 0);

  return (
    <section
      id="test"
      ref={testRef}
      className={slots.base()}
    >
      {step === 'start' && (
        <div
          className={slots.screen({ className: 'h-[600px] overflow-hidden' })}
        >
          <div className={slots.start()}>
            <div className={slots.startContent()}>
              <span className={slots.label()}>Диагностика кожи</span>
              <h3 className={slots.startTitle()}>
                Пройдите короткий тест и&nbsp;подберите уход по&nbsp;типу кожи
              </h3>
              <button
                type="button"
                className={slots.primaryBtn()}
                onClick={() => {
                  setStep('quiz');
                  setPage(0);
                  reachMetrikaGoal('start_quiz');
                }}
              >
                Начать тестирование
              </button>
            </div>
            <picture>
              <source
                srcSet={media('test/test-start-image-mobile.jpg')}
                media="(max-width: 767px)"
              />
              <img
                className={slots.startImage()}
                src={media('test/test-start-image-x4.jpg')}
                alt=""
              />
            </picture>
          </div>
        </div>
      )}

      {step === 'quiz' && (
        <div
          className={slots.screen({ className: slots.bg() })}
          style={bgStyle('test/test-bg-mobile.jpg', 'test/test-bg.jpg')}
        >
          <div className={slots.quiz()}>
            <button
              type="button"
              className={slots.back()}
              onClick={() => {
                if (page === 0) {
                  setStep('start');
                  return;
                }
                setPage(page - 1);
              }}
            >
              <SliderArrow className="size-6 rotate-180 text-[#34363C]" />
              <span className={slots.backText()}>Назад</span>
            </button>
            <div className={slots.question()}>
              <p className={slots.questionTitle()}>{questions[page].title}</p>
              <ul className={slots.answers()}>
                {questions[page].answers.map((answer, answerIndex) => {
                  const isActive = selected[page] === answerIndex;

                  return (
                    <li key={answerIndex}>
                      <button
                        type="button"
                        className={slots.answer({
                          className: isActive ? slots.answerActive() : undefined
                        })}
                        onClick={() => selectAnswer(answerIndex)}
                      >
                        <span className={slots.answerText()}>
                          {answer.title}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div className={slots.bottom()}>
              <p className={slots.note()}>
                Пройдите короткий тест <br className="hidden md:block" />и
                подберите уход по&nbsp;типу кожи
              </p>
              <ul className={slots.pages()}>
                {questions.map((_, index) => (
                  <li key={index}>
                    <button
                      type="button"
                      className={slots.page({
                        className:
                          index === page ? slots.pageActive() : undefined
                      })}
                      onClick={() => openPage(index)}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className={slots.next({
                  className: currentAnswered ? undefined : slots.nextDisabled()
                })}
                onClick={() => {
                  if (!currentAnswered) return;
                  if (page === questions.length - 1) {
                    showResult();
                    return;
                  }
                  setPage(page + 1);
                }}
              >
                <span className={slots.nextText()}>Дальше</span>
                <span className={slots.nextIcon()}>
                  <ArrowIcon className="rotate-180 text-white" />
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {step === 'thank' && (
        <div
          ref={thankRef}
          className={slots.screen({ className: slots.bg() })}
          style={bgStyle(
            'test/test-thank-bg-mobile.jpg',
            'test/test-bg-thank.jpg'
          )}
        >
          <div className={slots.thank()}>
            <img
              className={slots.thankLogo()}
              src={media('test/test-thank-logo.png')}
              alt=""
            />
            <p className={slots.thankTitle()}>Подобрали для вас уход</p>
            <div className={slots.thankBtns()}>
              <button
                type="button"
                className={slots.primaryBtn()}
                onClick={() => {
                  setResultsVisible(true);
                  reachMetrikaGoal('end_quiz');
                  pendingScroll.current = 'results';
                }}
              >
                Перейти к результатам
              </button>
              <button
                type="button"
                className={slots.primaryBtn({ className: slots.whiteBtn() })}
                onClick={restart}
              >
                Пройти заново
              </button>
            </div>
          </div>
        </div>
      )}

      <div
        ref={resultsRef}
        className={slots.results()}
      >
        <h3 className={slots.resultsTitle()}>Рекомендованная рутина</h3>
        <div className={slots.categories()}>
          {visibleCategories.map((category) => (
            <div
              key={category.category}
              className={slots.category({
                className:
                  category.items.length === 1
                    ? slots.categorySingle()
                    : slots.categoryMulti()
              })}
            >
              <p className={slots.categoryLabel()}>{category.name}</p>
              <div className="flex flex-col gap-6 md:hidden">
                {category.items.map((product, index) => (
                  <MobilePart
                    key={product.productId}
                    product={product}
                    index={index}
                  />
                ))}
              </div>
              <div className="hidden md:block">
                {category.items.length > 2 ? (
                  <>
                    <div className="flex flex-col gap-6 lg:hidden">
                      {category.items.map((product, index) => (
                        <ResultPart
                          key={product.productId}
                          product={product}
                          index={index}
                        />
                      ))}
                    </div>
                    <div className="hidden lg:block">
                      <CategorySlider items={category.items} />
                    </div>
                  </>
                ) : (
                  <div
                    className={
                      category.items.length > 1
                        ? slots.categoryList()
                        : undefined
                    }
                  >
                    {category.items.map((product, index) => (
                      <ResultPart
                        key={product.productId}
                        product={product}
                        index={index}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SliderNav({
  controls,
  prevLabel,
  nextLabel,
  compact = false
}: {
  controls: {
    prevBtnDisabled: boolean;
    nextBtnDisabled: boolean;
    onPrevButtonClick: () => void;
    onNextButtonClick: () => void;
  };
  prevLabel: string;
  nextLabel: string;
  compact?: boolean;
}) {
  const slots = styles();
  const btn = compact ? slots.mobileNavBtn : slots.categoryNavBtn;

  return (
    <div className={compact ? slots.mobileNav() : slots.categoryNav()}>
      <button
        type="button"
        aria-label={prevLabel}
        className={btn({ className: 'rotate-180' })}
        disabled={controls.prevBtnDisabled}
        onClick={controls.onPrevButtonClick}
      >
        <SliderArrow />
      </button>
      <button
        type="button"
        aria-label={nextLabel}
        className={btn()}
        disabled={controls.nextBtnDisabled}
        onClick={controls.onNextButtonClick}
      >
        <SliderArrow />
      </button>
    </div>
  );
}

function CategorySlider({ items }: { items: HydrabioTestProduct[] }) {
  const slots = styles();

  return (
    <UiKitCarouselBase
      className={slots.categorySlider()}
      loop={false}
      showArrows={false}
      slidesToScroll={1}
      renderCustomControls={(controls) => (
        <SliderNav
          controls={controls}
          prevLabel="Предыдущие товары"
          nextLabel="Следующие товары"
        />
      )}
    >
      {items.map((product, index) => (
        <ResultPart
          key={product.productId}
          product={product}
          index={index}
        />
      ))}
    </UiKitCarouselBase>
  );
}

function ProductCard({
  product,
  index
}: {
  product: HydrabioTestProduct;
  index: number;
}) {
  return (
    <UiLandingProductCardBioderma
      className="!w-full !min-w-0 text-black"
      classNameWrapper="!-mt-[13px] !mx-0 !w-full !max-w-none !px-[9px]"
      classNameImageWrapper="relative aspect-square !h-auto !w-full overflow-hidden [&_img]:absolute [&_img]:inset-0 [&_img]:size-full [&_img]:object-cover"
      classNameList="!min-h-0 max-md:!gap-[19px] lg:!gap-[13px]"
      product={product}
      positionIndex={index}
      onAddToCart={() => addLandingProductToCart(product)}
    />
  );
}

function MobilePart({
  product,
  index
}: {
  product: HydrabioTestProduct;
  index: number;
}) {
  const slots = styles();

  return (
    <UiKitCarouselBase
      className={slots.mobileSlider()}
      loop={false}
      showArrows={false}
      slidesToScroll={1}
      renderCustomControls={(controls) => (
        <SliderNav
          compact
          controls={controls}
          prevLabel="Предыдущий слайд"
          nextLabel="Следующий слайд"
        />
      )}
    >
      <ProductCard
        product={product}
        index={index}
      />
      {product.media.map((image) => (
        <div
          key={image.mobile}
          className={slots.mobileBanner()}
        >
          <img
            className="absolute inset-0 size-full object-cover"
            src={image.mobile}
            alt=""
          />
        </div>
      ))}
    </UiKitCarouselBase>
  );
}

function ResultPart({
  product,
  index
}: {
  product: HydrabioTestProduct;
  index: number;
}) {
  const slots = styles();

  return (
    <div className={slots.part()}>
      <ProductCard
        product={product}
        index={index}
      />
      <BannerSlider images={product.media} />
    </div>
  );
}

function BannerSlider({ images }: { images: HydrabioTestProduct['media'] }) {
  const slots = styles();
  const [index, setIndex] = useState(0);
  const multiple = images.length > 1;

  return (
    <UiKitCarouselBase
      className="relative min-w-0 self-start [--slide-size:100%]"
      isNested
      loop={multiple}
      showArrows={false}
      onSlidesChange={setIndex}
      renderCustomControls={
        multiple
          ? (controls) => (
              <div className={slots.counter()}>
                <button
                  type="button"
                  aria-label="Предыдущий слайд"
                  className={slots.counterBtn({ className: 'left-[14px]' })}
                  onClick={controls.onPrevButtonClick}
                >
                  <ArrowIcon />
                </button>
                <span className={slots.counterCurrent()}>{index + 1}</span>
                <span className={slots.counterAll()}>/{images.length}</span>
                <button
                  type="button"
                  aria-label="Следующий слайд"
                  className={slots.counterBtn({
                    className: 'right-[14px] rotate-180'
                  })}
                  onClick={controls.onNextButtonClick}
                >
                  <ArrowIcon />
                </button>
              </div>
            )
          : undefined
      }
    >
      {images.map((image) => (
        <picture key={image.desktop}>
          <source
            srcSet={image.mobile}
            media="(max-width: 767px)"
          />
          <img
            className={slots.banner()}
            src={image.desktop}
            alt=""
          />
        </picture>
      ))}
    </UiKitCarouselBase>
  );
}

function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      aria-hidden
      className={className}
    >
      <path
        d="M15 5 8 12l7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}
